import { useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { MoreHorizontal, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { PageHeader } from "@/components/shared/page-header";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMoney } from "@/features/clients/constants";
import { useClients } from "@/features/clients/api";
import { useDeleteSalesOrder, useSalesOrders } from "@/features/sales-orders/api";
import { SO_STATUSES, labelFor, statusTone } from "@/features/sales-orders/constants";
import type { SalesOrderListRow, SalesOrderStatus } from "@/features/sales-orders/types";
import { isSupabaseConfigured } from "@/lib/supabase";

export function SalesOrdersPage({ clientId: clientIdProp, embedded }: { clientId?: string; embedded?: boolean } = {}) {
  return (
    <ErrorBoundary title="Sales orders crashed">
      <SalesOrdersInner clientIdProp={clientIdProp} embedded={embedded} />
    </ErrorBoundary>
  );
}

function SalesOrdersInner({
  clientIdProp,
  embedded,
}: {
  clientIdProp?: string;
  embedded?: boolean;
}) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { client?: string };
  const clientFilter = clientIdProp ?? search.client ?? "all";

  const { data = [], isLoading, error, refetch, isFetching } = useSalesOrders(
    clientFilter !== "all" ? { clientId: clientFilter } : undefined,
  );
  const { data: clients = [] } = useClients();
  const remove = useDeleteSalesOrder();

  const [statusFilter, setStatusFilter] = useState<"all" | SalesOrderStatus>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState<SalesOrderListRow | null>(null);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return data.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (from && row.issue_date < from) return false;
      if (to && row.issue_date > to) return false;
      if (!query) return true;
      return [row.so_number, row.client_name, row.client_code, row.qt_number, row.client_po_ref]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [data, statusFilter, from, to, q]);

  const kpis = useMemo(() => {
    const open = data.filter((r) => ["draft", "confirmed", "in_progress", "partial"].includes(r.status));
    return {
      draft: data.filter((r) => r.status === "draft").length,
      openValue: open.reduce((s, r) => s + r.grand_total, 0),
      confirmed: data.filter((r) => r.status === "confirmed").length,
    };
  }, [data]);

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to use sales orders."
      />
    );
  }

  return (
    <div className="space-y-3">
      {!embedded ? (
        <PageHeader
          title="Sales orders"
          description="Client orders · GST · fulfillment in Phase 4.3"
          actions={
            <>
              <Button type="button" variant="outline" size="sm" disabled={isFetching} onClick={() => refetch()}>
                <RefreshCw className={isFetching ? "animate-spin" : ""} />
                Refresh
              </Button>
              <Button type="button" size="sm" onClick={() => navigate({ to: "/sales-orders/new" })}>
                <Plus />
                New order
              </Button>
            </>
          }
        />
      ) : null}

      {!embedded ? (
        <div className="flex flex-wrap items-end gap-2 text-xs">
          <div className="rounded border bg-card px-3 py-2">
            <span className="text-muted-foreground">Draft</span>{" "}
            <strong className="tabular-nums">{kpis.draft}</strong>
          </div>
          <div className="rounded border bg-card px-3 py-2">
            <span className="text-muted-foreground">Open pipeline</span>{" "}
            <strong className="tabular-nums">{formatMoney(kpis.openValue)}</strong>
          </div>
          <div className="rounded border bg-card px-3 py-2">
            <span className="text-muted-foreground">Confirmed</span>{" "}
            <strong className="tabular-nums">{kpis.confirmed}</strong>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-8 pl-7 text-xs"
            placeholder="Search SO no, client, QT…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {embedded || clientIdProp ? null : (
          <NativeSelect
            className="h-8 w-auto text-xs"
            value={clientFilter}
            onChange={(e) =>
              navigate({
                to: "/sales-orders",
                search: e.target.value === "all" ? {} : { client: e.target.value },
              })
            }
          >
            <option value="all">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} · {c.name}
              </option>
            ))}
          </NativeSelect>
        )}
        <NativeSelect
          className="h-8 w-auto text-xs"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
        >
          <option value="all">All statuses</option>
          {SO_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </NativeSelect>
        <Input type="date" className="h-8 w-36 text-xs" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Input type="date" className="h-8 w-36 text-xs" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>

      {isLoading ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <EmptyState title="Failed to load" description={(error as Error).message} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No sales orders"
          description="Create an order for a client or convert an accepted quotation."
          action={
            <Button size="sm" onClick={() => navigate({ to: "/sales-orders/new" })}>
              <Plus />
              New order
            </Button>
          }
        />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SO #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Issue</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>From QT</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row) => (
                <TableRow key={row.id} className="text-xs">
                  <TableCell>
                    <Link
                      to="/sales-orders/$soId"
                      params={{ soId: row.id }}
                      className="font-mono font-medium hover:underline"
                    >
                      {row.so_number}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-muted-foreground">{row.client_code}</span> {row.client_name}
                  </TableCell>
                  <TableCell>{format(parseISO(row.issue_date), "dd MMM yyyy")}</TableCell>
                  <TableCell>
                    {row.due_date ? format(parseISO(row.due_date), "dd MMM yyyy") : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-muted-foreground">{row.qt_number ?? "—"}</TableCell>
                  <TableCell>
                    <StatusDot tone={statusTone(row.status)}>
                      {labelFor(SO_STATUSES, row.status)}
                    </StatusDot>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatMoney(row.grand_total)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm">
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to="/sales-orders/$soId" params={{ soId: row.id }}>
                            Open
                          </Link>
                        </DropdownMenuItem>
                        {row.status === "draft" ? (
                          <>
                            <DropdownMenuItem asChild>
                              <Link to="/sales-orders/$soId/edit" params={{ soId: row.id }}>
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => setDeleting(row)}>
                              <Trash2 />
                              Delete draft
                            </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete draft sales order?"
        description={`${deleting?.so_number} will be permanently removed.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}
